import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { usePurchaseOrder } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import { PackageIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { Badge } from '@/components/ui/badge';
import { T, Var, useGT } from 'gt-react';

interface PurchaseOrderPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always INTERNAL_PURCHASE_ORDER
  entityId: string;
  className?: string;
}

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'default',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

export function PurchaseOrderPreview({
  entityId,
  className,
}: PurchaseOrderPreviewProps) {
  const gt = useGT();
  const {
    data: purchaseOrderData,
    isLoading,
    error,
  } = usePurchaseOrder(entityId);

  const statusLabels: Record<string, string> = {
    DRAFT: gt('Draft', { context: 'purchase order status' }),
    PENDING_APPROVAL: gt('Pending Approval', {
      context: 'purchase order status',
    }),
    APPROVED: gt('Approved', { context: 'purchase order status' }),
    SENT: gt('Sent', { context: 'purchase order status' }),
    PARTIALLY_RECEIVED: gt('Partially Received', {
      context: 'purchase order status',
    }),
    FULLY_RECEIVED: gt('Fully Received', { context: 'purchase order status' }),
    PARTIALLY_INVOICED: gt('Partially Invoiced', {
      context: 'purchase order status',
    }),
    FULLY_INVOICED: gt('Fully Invoiced', { context: 'purchase order status' }),
    CLOSED: gt('Closed', { context: 'purchase order status' }),
    CANCELLED: gt('Cancelled', { context: 'purchase order status' }),
  };

  const formatCurrencyValue = (
    value: string | undefined | null,
    currency = 'USD',
  ): string => {
    if (!value) return gt('N/A', { context: 'not applicable abbreviation' });

    const numeric = Number(value);
    if (Number.isNaN(numeric))
      return gt('N/A', { context: 'not applicable abbreviation' });

    return numeric.toLocaleString(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    });
  };

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <PackageIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="purchase order preview title">Purchase Order</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="loading state">Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !purchaseOrderData?.purchaseOrder) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <PackageIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="purchase order preview title">Purchase Order</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="error loading purchase order preview">
            Unable to load purchase order details
          </T>
        </p>
      </div>
    );
  }

  const purchaseOrder = purchaseOrderData.purchaseOrder;
  const lineItems = purchaseOrderData.lineItems || [];

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <PackageIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">
          {purchaseOrder.externalId || (
            <T>
              PO-<Var>{purchaseOrder.internalId}</Var>
            </T>
          )}
        </h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div className="flex items-center justify-between">
          <Badge
            variant={STATUS_VARIANTS[purchaseOrder.status] || 'outline'}
            className="text-xs"
          >
            {statusLabels[purchaseOrder.status] ?? (
              <Var>{String(purchaseOrder.status)}</Var>
            )}
          </Badge>
        </div>

        {purchaseOrder.totalAmount && (
          <div>
            <span className="text-muted-foreground">
              <T context="PO field label">Total: </T>
            </span>
            <span className="font-medium">
              {formatCurrencyValue(
                purchaseOrder.totalAmount,
                purchaseOrder.currency || 'USD',
              )}
            </span>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">
            <T context="PO field label">Order Date: </T>
          </span>
          <span className="font-medium">
            {formatDateOnlyString(purchaseOrder.orderDate)}
          </span>
        </div>

        {purchaseOrder.deliveryDate && (
          <div>
            <span className="text-muted-foreground">
              <T context="PO field label">Delivery Date: </T>
            </span>
            <span className="font-medium">
              {formatDateOnlyString(purchaseOrder.deliveryDate)}
            </span>
          </div>
        )}

        {lineItems.length > 0 && (
          <div>
            <span className="text-muted-foreground">
              <T context="PO field label">Line Items: </T>
            </span>
            <span className="font-medium">{lineItems.length}</span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={`/accounts-payable/purchase-orders/${purchaseOrder.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          <T context="link to full purchase order">View Details</T>
        </a>
      </div>
    </div>
  );
}
